import type { QueryClient } from '@tanstack/react-query';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BackendTypes, GetResponse } from '@tf/api';
import { BackendClient, gql, GraphQLClient } from '@tf/api';
import {
	createPublicSchemaId,
	objectRelationValue,
	profileRelationValue,
	splitObjectId,
	T,
} from '@tf/utils';

import { getObjectUriByNodeId } from '@/core/api/legacy';
import { getCaseIdAlias } from '@/core/api/utils/getCaseIdAlias';
import { getLinkedObjectIdFromMutationResult } from '@/core/api/utils/getLinkedObjectIdFromMutationResult';
import type { KeyValueFilter } from '@/core/api/utils/typeUtils';
import { useClusterId } from '@/core/appMeta';
import type {
	Case,
	CaseKind,
	CaseRequest,
	CaseWithData,
	CaseWithRequestMetadata,
	CaseWithRequests,
} from '@/core/types/cases';

const profileCasesQueryOptions = (profileId: string) => {
	return queryOptions({
		queryKey: ['profileCases', profileId],
		queryFn: async (): Promise<CaseWithRequestMetadata[]> => {
			const casesData = await GraphQLClient.getInstance().request<
				GetResponse<Case>,
				{ profileId: string }
			>(
				gql`
					query ProfileCases($profileId: JSON!) {
						get(
							rootFilter: {
								archetype: "case"
								meta: {
									AND: [
										{ key: "/@with/profile", operator: EQ, value: $profileId }
										{ key: "/@with/payment", operator: EQ, value: null }
									]
								}
							}
							paginationFilter: {
								limit: 1000
								offset: 0
								sortByMeta: "/@createdAt"
								sortByDirection: DESC
							}
						) {
							id
							aliases
							kind
							meta(path: ["/@author", "/@createdAt"])
							labels
						}
					}
				`,
				{ profileId }
			);

			if (casesData.get.length === 0) {
				// Early return to prevent error with empty filters in the next request
				return [];
			}

			const caseLinkFilters = casesData.get.map(
				(c): KeyValueFilter => ({
					key: '/@with/case',
					operator: 'EQ',
					value: c.id,
				})
			);

			const caseRequests = await GraphQLClient.getInstance().request<
				GetResponse<CaseWithRequestMetadata['request']>,
				{ caseLinkFilters: KeyValueFilter[] }
			>(
				gql`
					query CaseRequestsForCases($caseLinkFilters: [KeyValueFilter!]!) {
						get(rootFilter: { kind: "case_request", meta: { OR: $caseLinkFilters } }) {
							id
							meta(path: ["/@with/case"])
							labels(path: ["/status", "/sent_at"])
						}
					}
				`,
				{ caseLinkFilters }
			);

			return casesData.get.map((c) => {
				const request = caseRequests.get.find((r) => r.meta.case.includes(c.id));
				T.assertNotNullish(request, `no request found for case ${c.id}`);

				return {
					case: c,
					request,
				};
			});
		},
	});
};

export const useProfileCases = (profileId: string) => {
	return useQuery(profileCasesQueryOptions(profileId));
};

export const useProfileCasesCount = (profileId: string) => {
	return useQuery({
		// Extends the list query key, so will be invalidated together
		queryKey: [...profileCasesQueryOptions(profileId).queryKey, 'count'],
		queryFn: async (): Promise<number> => {
			const casesData = await GraphQLClient.getInstance().request<
				GetResponse<{ id: string }>,
				{ profileId: string }
			>(
				gql`
					query ProfileCasesIds($profileId: JSON!) {
						get(
							rootFilter: {
								archetype: "case"
								meta: {
									AND: [
										{ key: "/@with/profile", operator: EQ, value: $profileId }
										{ key: "/@with/payment", operator: EQ, value: null }
									]
								}
							}
						) {
							id
						}
					}
				`,
				{ profileId }
			);

			return casesData.get.length;
		},
	});
};

const caseWithRequestsQueryOptions = (caseObjectName: string) => {
	return queryOptions({
		queryKey: ['case', caseObjectName],
		queryFn: async (): Promise<CaseWithRequests> => {
			const caseRes = await GraphQLClient.getInstance().request<
				GetResponse<CaseWithData>,
				{ caseObjectName: string }
			>(
				gql`
					query Case($caseObjectName: String!) {
						get(rootFilter: { uuid: $caseObjectName }) {
							id
							aliases
							kind
							meta(path: ["/@author", "/@createdAt"])
							labels
							data
						}
					}
				`,
				{ caseObjectName }
			);

			const caseRequestsRes = await GraphQLClient.getInstance().request<
				GetResponse<CaseRequest>,
				{ parentCaseId: string }
			>(
				gql`
					query CaseRequests($parentCaseId: JSON!) {
						get(
							rootFilter: {
								kind: "case_request"
								meta: { AND: [{ key: "/@with/case", operator: EQ, value: $parentCaseId }] }
							}
						) {
							id
							meta(path: ["/@author", "/@createdAt", "/responded_at"])
							labels
							data
							response_files: nestedRelation(filter: { relationKind: "case_request_attachment" }) {
								object {
									id
									meta(path: ["/@filename", "/@mime", "/@size", "/@createdAt"])
									archetype
									kind
								}
							}
						}
					}
				`,
				{ parentCaseId: caseRes.get[0].id }
			);

			return {
				case: caseRes.get[0],
				requests: caseRequestsRes.get,
			};
		},
	});
};

const invalidateCaseWithRequestsQueryById = (queryClient: QueryClient, caseId: string) => {
	const { objectName } = splitObjectId(caseId);
	return queryClient.invalidateQueries(caseWithRequestsQueryOptions(objectName));
};

export const useCaseWithRequests = (caseObjectName: string) => {
	return useQuery(caseWithRequestsQueryOptions(caseObjectName));
};

type CreateClientCaseRequestParams = {
	profileId: string;
	unitNodeId: BackendTypes.GraphId;
	recipientEmail: string;
	requestKind: CaseKind;
};

type CreateLivenessCheckData = {
	'@id': string;
};

export const useCreateCaseMutation = () => {
	const queryClient = useQueryClient();
	const clusterId = useClusterId();

	return useMutation({
		mutationFn: async ({
			profileId,
			unitNodeId,
			recipientEmail,
			requestKind,
		}: CreateClientCaseRequestParams): Promise<Case[]> => {
			let livenessCheckId;
			if (requestKind === 'identity_verification') {
				const unitObjectId = await getObjectUriByNodeId(unitNodeId);
				const res = await BackendClient.getInstance().createLivenessCheck({
					serviceKind: 'idenfy',
					objectId: unitObjectId,
				});
				const data: CreateLivenessCheckData = await res.json();
				livenessCheckId = data['@id'];
			}

			const res = await GraphQLClient.getInstance().request(
				gql`
					mutation CreateCase($objectsToCreate: [ObjectInput]) {
						mutate(create: $objectsToCreate) {
							id
							aliases
							archetype
							kind
							meta(path: ["/@author", "/@createdAt"])
							labels
						}
					}
				`,
				{
					objectsToCreate: [
						{
							id: 'new://case_1',
							schema: createPublicSchemaId({
								clusterId,
								schemaTag: `types/general/case/${requestKind}`,
							}),
							aliases: {
								[getCaseIdAlias()]: '@displayName',
							},
							labels: {
								recipient_email: recipientEmail,
								status: 'ACTIVE',
							},
							data: {
								profile: profileRelationValue(profileId),
								...(livenessCheckId
									? { liveness_check: objectRelationValue('liveness_check', livenessCheckId) }
									: {}),
							},
						},
						{
							id: 'new://case_request_1',
							schema: createPublicSchemaId({
								clusterId,
								schemaTag: 'types/general/case/case_request',
							}),
							labels: {
								status: 'DRAFT',
							},
							data: {
								profile: profileRelationValue(profileId),
								case: objectRelationValue('case', 'new://case_1'),
								request_text: '',
							},
						},
					],
				}
			);

			return res.mutate.filter((obj: { archetype: string }) => obj.archetype === 'case');
		},
		onSuccess: async (_, { profileId }) => {
			return queryClient.invalidateQueries(profileCasesQueryOptions(profileId));
		},
	});
};

export const useUpdateCaseClientEmailMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ caseId, email }: { caseId: string; email: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation UpdateCaseEmail($caseId: ID!, $email: JSON!) {
						mutate(
							patch: [
								{
									objectId: $caseId
									objectLabelsPatch: [{ op: "add", path: "/recipient_email", value: $email }]
								}
							]
						) {
							id
							meta(path: ["/@with/profile"])
						}
					}
				`,
				{ caseId, email }
			);
		},
		onSuccess: (result: Record<'mutate', any>, { caseId }) => {
			const profileId = getLinkedObjectIdFromMutationResult(result, caseId, 'profile');
			return Promise.all([
				invalidateCaseWithRequestsQueryById(queryClient, caseId),
				queryClient.invalidateQueries(profileCasesQueryOptions(profileId)),
			]);
		},
	});
};

const useCaseRequestSuccessUpdateHandler = () => {
	const queryClient = useQueryClient();

	return (result: Record<'mutate', any>, { caseRequestId }: { caseRequestId: string }) => {
		const caseId = getLinkedObjectIdFromMutationResult(result, caseRequestId, 'case');
		return invalidateCaseWithRequestsQueryById(queryClient, caseId);
	};
};

export const useSendCaseRequestMutation = () => {
	const onSuccess = useCaseRequestSuccessUpdateHandler();
	return useMutation({
		mutationFn: async ({ caseRequestId }: { caseRequestId: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation SendCaseRequest($caseRequestId: ID!, $sentAtDate: JSON!) {
						mutate(
							patch: [
								{
									objectId: $caseRequestId
									objectLabelsPatch: [{ op: "add", path: "/sent_at", value: $sentAtDate }]
								}
							]
						) {
							id
							meta(path: ["/@with/case"])
						}
					}
				`,
				{ caseRequestId, sentAtDate: new Date().toISOString() }
			);
		},
		onSuccess,
	});
};

export const useUpdateCaseRequestTextMutation = () => {
	const onSuccess = useCaseRequestSuccessUpdateHandler();
	return useMutation({
		mutationFn: async ({ caseRequestId, text }: { caseRequestId: string; text: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation UpdateCaseRequestText($caseRequestId: ID!, $text: JSON!) {
						mutate(
							patch: [
								{
									objectId: $caseRequestId
									objectDataPatch: [{ op: "add", path: "/request_text", value: $text }]
								}
							]
						) {
							id
							meta(path: ["/@with/case"])
						}
					}
				`,
				{ caseRequestId, text }
			);
		},
		onSuccess,
	});
};

export const useUpdateCaseRequestDueDateMutation = () => {
	const onSuccess = useCaseRequestSuccessUpdateHandler();
	return useMutation({
		mutationFn: async ({ caseRequestId, date }: { caseRequestId: string; date: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation UpdateCaseRequestDueDate($caseRequestId: ID!, $date: JSON!) {
						mutate(
							patch: [
								{
									objectId: $caseRequestId
									objectLabelsPatch: [{ op: "add", path: "/due_date", value: $date }]
								}
							]
						) {
							id
							meta(path: ["/@with/case"])
						}
					}
				`,
				{ caseRequestId, date }
			);
		},
		onSuccess,
	});
};

export const useRevokeCaseRequestMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			caseId,
			caseRequestId,
			paymentObjectId,
		}: {
			caseId: string;
			caseRequestId: string;
			paymentObjectId?: string;
		}) => {
			// Note: while we don't have a ping-pong (multiple requests inside a single case), revoking a request should close the case
			const revokeCaseResult = await GraphQLClient.getInstance().request(
				gql`
					mutation RevokeCaseRequest($caseId: ID!, $caseRequestId: ID!) {
						mutate(
							patch: [
								{
									objectId: $caseId
									objectLabelsPatch: [{ op: "add", path: "/status", value: "CLOSED" }]
								}
								{
									objectId: $caseRequestId
									objectLabelsPatch: [{ op: "add", path: "/status", value: "REVOKED" }]
								}
							]
						) {
							id
							meta(path: ["/@with/case"])
						}
					}
				`,
				{
					caseId,
					caseRequestId,
				}
			);

			if (paymentObjectId) {
				await updatePaymentStatus(paymentObjectId);
			}

			return revokeCaseResult;
		},
		onSuccess: (_, { caseId }) => {
			return Promise.all([
				queryClient.invalidateQueries({ queryKey: ['profileCases'] }),
				invalidateCaseWithRequestsQueryById(queryClient, caseId),
			]);
		},
	});
};

export const useCloseCaseMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ caseId, paymentObjectId }: { caseId: string; paymentObjectId?: string }) => {
			const closeCaseResult = await GraphQLClient.getInstance().request(
				gql`
					mutation CloseCase($caseId: ID!) {
						mutate(
							patch: [
								{
									objectId: $caseId
									objectLabelsPatch: [{ op: "add", path: "/status", value: "CLOSED" }]
								}
							]
						) {
							id
						}
					}
				`,
				{ caseId }
			);

			if (paymentObjectId) {
				await updatePaymentStatus(paymentObjectId);
			}

			return closeCaseResult;
		},
		onSuccess: (_, { caseId }) => {
			return Promise.all([
				queryClient.invalidateQueries({ queryKey: ['profileCases'] }),
				invalidateCaseWithRequestsQueryById(queryClient, caseId),
			]);
		},
	});
};

const updatePaymentStatus = async (paymentObjectId: string) => {
	const casesData = await GraphQLClient.getInstance().request<
		GetResponse<Case>,
		{ paymentObjectId: string }
	>(
		gql`
			query PaymentCases($paymentObjectId: JSON!) {
				get(
					rootFilter: {
						archetype: "case"
						meta: { AND: [{ key: "/@with/payment", operator: CONTAINS, value: $paymentObjectId }] }
					}
					paginationFilter: { limit: 1000, offset: 0, sortByMeta: "/@createdAt", sortByDirection: DESC }
				) {
					id
					labels
				}
			}
		`,
		{ paymentObjectId }
	);

	const allCasesClosed =
		casesData.get.length > 0 && casesData.get.every((case_) => case_.labels.status === 'CLOSED');

	if (allCasesClosed) {
		const statusChangedAt = new Date().toISOString();
		await GraphQLClient.getInstance().request(
			gql`
				mutation UpdatePaymentStatus($paymentObjectId: ID!) {
					mutate(
						patch: [
							{
								objectId: $paymentObjectId
								objectMetaPatch: [
									{ op: "replace", path: "/@status", value: "PENDING_REVIEW" }
									{ op: "add", path: "/@status_changed_at", value: statusChangedAt }
								]
							}
						]
					) {
						id
					}
				}
			`,
			{ paymentObjectId }
		);
	}
};
